import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Plug, Handshake, ArrowRight } from "lucide-react";
import { FadeUp, Overline } from "@/components/FadeUp";
import EnquiryForm from "@/components/EnquiryForm";
import { ASSETS } from "@/lib/api";
import { Button } from "@/components/ui/button";

const POINT_ICONS = [Building2, Plug, Handshake];

export default function Fleet() {
  const { t } = useTranslation();
  const cards = t("fleetPage.cards", { returnObjects: true });
  return (
    <div data-testid="fleet-page">
      <section className="relative bg-[#EAF0F6] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <FadeUp>
            <Overline>{t("fleetPage.overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A2540]">
              {t("fleetPage.title")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              {t("fleetPage.subtitle")}
            </p>
            <Button asChild className="mt-8 rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3">
              <a href="#flotten-formular" data-testid="fleet-scroll-btn">{t("fleetPage.scrollBtn")}</a>
            </Button>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="border border-slate-200 bg-white p-2">
              <img src={ASSETS.fleet} alt="Skyworth Flotte" data-testid="fleet-hero-image"
                   className="w-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.isArray(cards) && cards.map((p, i) => {
              const Icon = POINT_ICONS[i];
              return (
                <FadeUp key={p.title} delay={i * 0.1}>
                  <div className="border border-slate-200 p-8 h-full bg-white">
                    <Icon className="w-8 h-8 text-[#0052CC]" />
                    <h3 className="mt-5 text-xl font-semibold text-[#0A2540]">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <FadeUp>
            <Overline>{t("fleetPage.vans.overline")}</Overline>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#0A2540] max-w-2xl">
              {t("fleetPage.vans.title")}
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t("fleetPage.vans.text")}
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <iframe
                  src="https://www.youtube-nocookie.com/embed/j2daTBkcpp4"
                  title="Skyworth HONGTU Cargo Van – Offizielles Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="fleet-youtube"
                  className="w-full border border-slate-200 aspect-video"
                />
                <p className="mt-2 text-sm font-semibold text-slate-600">{t("fleetPage.vans.cargoLabel")}</p>
              </div>
              <div>
                <iframe
                  src="https://www.youtube-nocookie.com/embed/eaSBDT5fAlc"
                  title="Skyworth HONGTU Passenger Van – Offizielles Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="fleet-youtube-passenger"
                  className="w-full border border-slate-200 aspect-video"
                />
                <p className="mt-2 text-sm font-semibold text-slate-600">{t("fleetPage.vans.passengerLabel")}</p>
              </div>
            </div>
            <Button asChild className="mt-6 rounded-md bg-[#0A2540] hover:bg-slate-800 text-white px-8 py-3">
              <Link to="/modelle/hongtu" data-testid="fleet-hongtu-link">
                {t("fleetPage.vans.button")} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </FadeUp>
        </div>
      </section>

      <section id="flotten-formular" className="bg-[#F4F7F9] border-t border-slate-200 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <FadeUp>
            <Overline>{t("fleetPage.formSection.overline")}</Overline>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#0A2540]">
              {t("fleetPage.formSection.title")}
            </h2>
            <div className="mt-8 border border-slate-200 bg-white p-8 md:p-12">
              <EnquiryForm type="flotte" />
            </div>
            <p className="mt-6 text-sm text-slate-600 flex items-center gap-2">
              {t("fleetPage.personal")}
              <Link to="/probefahrt" className="font-semibold text-[#0052CC] inline-flex items-center gap-1">
                {t("fleetPage.personalLink")} <ArrowRight className="w-4 h-4" />
              </Link>
            </p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
